
import { InterchangeEventTypes } from '@/schema/interchange_event';
import type { InterchangeEventRow } from './types';
import Vue, { PropType } from 'vue';

type PeppolAlertType = 'success' | 'warning' | 'info' | 'error';

type PeppolFieldKey = 'httpStatus' | 'detail' | 'message' | 'reason' | 'title';

type PeppolValidationError = {
	id: string;
	level: string;
	fieldName: string;
	description: string;
};

type PeppolInterpretation =
	| { rule: 'delivery'; alertType: PeppolAlertType; titleKey: string }
	| { rule: 'bounce-403'; alertType: 'error'; titleKey: string; fields: { key: PeppolFieldKey; value: string }[] }
	| { rule: 'bounce-400'; alertType: 'error'; titleKey: string; errors: PeppolValidationError[] }
	| { rule: 'processing'; alertType: PeppolAlertType; titleKey: string; detail?: string }
	| { rule: 'transit'; alertType: 'info'; titleKey: string }
	| { rule: 'unknown'; statusText?: string | null };

type RawDataShape = {
	api_response?: {
		status?: string | number;
		body?: unknown;
	};
};

function getRaw(row: InterchangeEventRow): RawDataShape | undefined {
	const r = row.raw_data;
	if (r && typeof r === 'object') {
		return r as RawDataShape;
	}
	return undefined;
}

function getHttpStatus(row: InterchangeEventRow): number | null {
	const s = getRaw(row)?.api_response?.status;
	if (s === undefined || s === null) {
		return null;
	}
	const n = typeof s === 'number' ? s : parseInt(String(s), 10);
	return Number.isFinite(n) ? n : null;
}

function getApiBody(row: InterchangeEventRow): Record<string, unknown> | null {
	const b = getRaw(row)?.api_response?.body;
	if (b && typeof b === 'object' && !Array.isArray(b)) {
		return b as Record<string, unknown>;
	}
	return null;
}

function isPermanentBounce(subtype: string | null | undefined): boolean {
	if (!subtype) {
		return false;
	}
	return subtype === 'PERMANENT' || subtype.includes('PERMANENT');
}

function stringField(body: Record<string, unknown>, ...keys: string[]): { key: PeppolFieldKey; value: string } | null {
	for (const k of keys) {
		const v = body[k];
		if (typeof v === 'string' && v.trim()) {
			const keyMap: Record<string, PeppolFieldKey> = {
				detail: 'detail',
				message: 'message',
				reason: 'reason',
				title: 'title',
				error: 'message',
				description: 'detail'
			};
			return { key: keyMap[k] || 'detail', value: v };
		}
	}
	return null;
}

function bounce403Fields(row: InterchangeEventRow): { key: PeppolFieldKey; value: string }[] {
	const fields: { key: PeppolFieldKey; value: string }[] = [];
	const status = getHttpStatus(row);
	if (status !== null) {
		fields.push({ key: 'httpStatus', value: String(status) });
	}
	const body = getApiBody(row);
	if (body) {
		const extra = stringField(body, 'detail', 'message', 'reason', 'title', 'error', 'description');
		if (extra) {
			fields.push(extra);
		}
	}
	return fields;
}

function normalizeValidationError(e: unknown): PeppolValidationError | null {
	if (!e || typeof e !== 'object') {
		return null;
	}
	const o = e as Record<string, unknown>;
	return {
		id: String(o.id ?? o.errorId ?? '—'),
		level: String(o.level ?? o.severity ?? '—'),
		fieldName: String(o.fieldName ?? o.field ?? o.path ?? '—'),
		description: String(o.description ?? o.message ?? o.text ?? '—')
	};
}

function parseValidationErrors(row: InterchangeEventRow): PeppolValidationError[] {
	const body = getApiBody(row);
	if (!body) {
		return [];
	}
	const lists: unknown[] = [body.errors, body.validationErrors, body.validation_errors].filter(v => v !== undefined);
	for (const list of lists) {
		if (!Array.isArray(list)) {
			continue;
		}
		const out = list.map(normalizeValidationError).filter((x): x is PeppolValidationError => x !== null);
		if (out.length) {
			return out;
		}
	}
	return [];
}

function processingAlertType(subtype: string | null | undefined): PeppolAlertType {
	const s = (subtype || '').toUpperCase();
	if (/(REJECT|DENIED|REFUSED|ERROR)/.test(s)) {
		return 'error';
	}
	if (/(ACCEPT|APPROVED|SUCCESS|OK)/.test(s)) {
		return 'success';
	}
	return 'warning';
}

function interpretPeppolEvent(row: InterchangeEventRow): PeppolInterpretation {
	const eventType = row.event_type;
	const sub = row.event_subtype || '';

	if (eventType === InterchangeEventTypes.DELIVERY && sub === 'TRANSPORT_ACK_RECEIVED_Delivery') {
		return { rule: 'delivery', alertType: 'success', titleKey: 'interchangeEventDetail.peppol.deliveryTitle' };
	}

	if (eventType === InterchangeEventTypes.BOUNCE && isPermanentBounce(sub)) {
		const http = getHttpStatus(row);
		if (http === 403) {
			return {
				rule: 'bounce-403',
				alertType: 'error',
				titleKey: 'interchangeEventDetail.peppol.bounce403Title',
				fields: bounce403Fields(row)
			};
		}
		if (http === 400) {
			return {
				rule: 'bounce-400',
				alertType: 'error',
				titleKey: 'interchangeEventDetail.peppol.bounce400Title',
				errors: parseValidationErrors(row)
			};
		}
	}

	if (eventType === InterchangeEventTypes.PROCESSING && sub.startsWith('INVOICE_RESPONSE_RECEIVED_')) {
		return {
			rule: 'processing',
			alertType: processingAlertType(sub),
			titleKey: 'interchangeEventDetail.peppol.processingTitle',
			detail: sub
		};
	}

	if (eventType === InterchangeEventTypes.TRANSIT) {
		return { rule: 'transit', alertType: 'info', titleKey: 'interchangeEventDetail.peppol.transitTitle' };
	}

	const raw = getRaw(row);
	const statusText =
		raw?.api_response?.status !== undefined && raw?.api_response?.status !== null
			? String(raw.api_response.status)
			: null;

	return {
		rule: 'unknown',
		statusText
	};
}

export default Vue.extend({
	name: 'PeppolInterpreter',
	props: {
		row: {
			type: Object as PropType<InterchangeEventRow>,
			required: true
		}
	},
	computed: {
		interpretation() {
			return interpretPeppolEvent(this.row);
		}
	},
	methods: {
		fieldLabel(key: PeppolFieldKey): string {
			switch (key) {
				case 'httpStatus':
					return this.$t('interchangeEventDetail.peppol.fieldLabels.httpStatus') as string;
				case 'detail':
					return this.$t('interchangeEventDetail.peppol.fieldLabels.detail') as string;
				case 'message':
					return this.$t('interchangeEventDetail.peppol.fieldLabels.message') as string;
				case 'reason':
					return this.$t('interchangeEventDetail.peppol.fieldLabels.reason') as string;
				case 'title':
					return this.$t('interchangeEventDetail.peppol.fieldLabels.title') as string;
				default:
					return key;
			}
		}
	}
});
